"use client";

import SearchIcon from "@/components/core/icons/SearchIcon";
import { useCommandK } from "@/helpers/CommandK";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import type { GuidesTopNavigation } from "@/app/(core)/guides/types";
import clsx from "clsx";
import { KnowledgeTopNavigation } from "@/app/(core)/knowledge/types";

export default function PageTopNavigation({
  navigation,
  basePath,
  currentPageBreadcrumb,
}: {
  navigation?: GuidesTopNavigation | KnowledgeTopNavigation;
  basePath: string;
  currentPageBreadcrumb?: string;
}) {
  const { show } = useCommandK();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const category = searchParams.get("category");
  const line = useRef<HTMLDivElement>(null);
  const breadcrumbs = pathname.split("/").filter((path) => path !== "");
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    const activeElement = document.querySelector(
      `#doc-nav-link[href="/${basePath}${
        pathname.split("/")[2] ? "/" + pathname.split("/")[2] : ""
      }"]`
    ) as HTMLAnchorElement;
    if (activeElement && line.current) {
      line.current.style.width = `${activeElement.offsetWidth - 16}px`;
      line.current.style.transform = `translateX(${
        activeElement.offsetLeft + 8
      }px)`;
    }
    setPageTitle(document.title);
  }, [pathname, basePath]);

  const isOverview = pathname === `/${basePath}` && category === null;

  return (
    <nav className="w-full border-b border-b-grey-850 relative flex items-center justify-between gap-2 lg:gap-8 z-10 lg:overflow-visible px-2 lg:px-0 overflow-y-visible overflow-x-auto custom-scrollbar">
      <div className="flex lg:gap-6 items-center w-full">
        <div className="flex items-center gap-2 -ml-2 lg:w-[330px] shrink-0">
          <Link
            href={"/" + basePath}
            id="doc-nav-link"
            className={clsx(
              "leading-none p-2 text-sm rounded font-medium mb-2 transition-colors duration-150",
              !isOverview && "lg:hover:bg-grey-850"
            )}
          >
            Overview
          </Link>
        </div>
        <div className="w-full h-full mb-2 items-center gap-1 hidden lg:flex">
          {breadcrumbs.map((breadcrumb, index) => {
            if (breadcrumb === "guide" || breadcrumbs.length === 1) {
              return undefined;
            }

            const isLast = index === breadcrumbs.length - 1;

            const breadcrumbFromTitle = pageTitle?.split(' · ')[0] || "";
            const breadcrumbItemTitle = navigation?.right?.find(
              (nav) => nav.slug === breadcrumb
            )?.title;

            const slugBreadCrumb = breadcrumb.split("-").join(" ");

            if (isLast) {
              return (
                <span
                  key={index}
                  className={clsx(
                    "font-medium text-sm truncate line-clamp-1 text-white",
                    !currentPageBreadcrumb && "capitalize"
                  )}
                >
                  {currentPageBreadcrumb || breadcrumbFromTitle || breadcrumbItemTitle || slugBreadCrumb}
                </span>
              );
            }

            return (
              <React.Fragment key={index}>
                <Link
                  href={"/" + breadcrumbs.slice(0, index + 1).join("/")}
                  className={clsx(
                    "font-medium text-sm truncate capitalize line-clamp-1 text-grey hover:text-white transition-colors duration-150"
                  )}
                >
                  {breadcrumbItemTitle || slugBreadCrumb}
                </Link>
                {index < breadcrumbs.length - 1 && (
                  <span className="text-grey-600">/</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="flex flex-row-reverse lg:flex-row items-center gap-2 -mr-2 shrink-0 mb-2">
        <button
          onClick={show}
          className="bg-grey-850 group hidden lg:flex w-auto rounded pl-1.5 p-1 lg:mr-4 gap-2 items-center max-[319px]:justify-center max-[319px]:aspect-square max-[319px]:gap-2 justify-between text-grey transition-colors duration-150 hover:text-grey-400 hover:bg-grey-800"
        >
          <div className="shrink-0 h-4 aspect-square flex items-center justify-center">
            <SearchIcon />
          </div>
          <div className="flex items-center gap-0 max-lg:hidden p-1 bg-grey-800 rounded-sm group-hover:bg-grey-700 transition-colors duration-100 group-hover:text-white">
            <p className="leading-none text-[14px] font-medium">⌘K</p>
          </div>
        </button>
        {navigation && navigation.right?.map((nav, index) => (
          <Link
            onMouseEnter={() => router.prefetch(nav.slug)}
            href={nav.slug}
            id="doc-nav-link"
            key={index}
            className={clsx(
              "leading-none p-2 text-sm rounded font-medium transition-colors duration-150",
              pathname !== nav.slug && "lg:hover:bg-grey-850"
            )}
          >
            {nav.title}
          </Link>
        ))}
      </div>

      <div
        ref={line}
        className={clsx(
          "absolute bottom-0 w-18 bg-grey-850 h-px lg:-mb-px lg:ml-auto -ml-2 transition-all duration-200 outline-4 outline-grey-950",
          isOverview && "bg-white outline"
        )}
      />
    </nav>
  );
}
